/**
 * React Query hook for Timer List (Global)
 * Used by TimerManager in Layout to fetch active time entries
 * Provides automatic caching to prevent redundant API calls on navigation
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import moment from 'moment';
import { listTimeEntries } from 'services/timerServices';
import { queryKeys } from '../config/queryClient';

const EMPTY_ARRAY = [];

/**
 * Fetches timer list (active/in-progress time entries) for the current user
 *
 * @param {string} userId - The cognito username of the current user
 * @returns {Promise<Array>} - Array of time entries sorted: running first, then paused
 */
const fetchTimerList = async (userId) => {
  try {
    const response = await listTimeEntries(
      {
        case_id: null,
        time_entry_date: null,
        time_entry_status: 'PROGRESS',
        time_entry_from: moment(new Date()).startOf('day').format('YYYY-MM-DDTHH:mm:ss+00:00'),
        time_entry_to: moment(moment().add(1, 'month').toDate()).endOf('day').format('YYYY-MM-DDTHH:mm:ss+00:00'),
        time_entry_filter: 0,
        search_key: '',
        firm_user_id: userId ? [userId] : '',
      },
      false,
      undefined,
      undefined,
      undefined,
      undefined,
      true
    );

    if (response?.data?.time_entries) {
      const running = response.data.time_entries.filter((entry) => !entry.end_time);
      const remaining = response.data.time_entries.filter((entry) => entry.end_time);
      return [...running, ...remaining];
    }

    return [];
  } catch (error) {
    console.error('Error fetching timer list:', error);
    throw error;
  }
};

/**
 * Hook to fetch active timer list with React Query
 * Caches the timer list to prevent redundant API calls on page navigation
 *
 * @param {Object} options - Configuration options
 * @param {string} options.userId - The cognito username of the current user
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 *
 * @returns {Object} - timerEntries, isLoading, error, refetch, isFetching, invalidateTimerList
 */
export const useTimerListQuery = ({ userId = '', enabled = true } = {}) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: queryKeys.timerList.list(userId),
    queryFn: () => fetchTimerList(userId),
    enabled: enabled && !!userId,
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
    retry: 2, // Retry twice on failure
  });

  /**
   * Invalidate the timer list cache, triggering a refetch
   * Use this after mutations (create/delete/save timers)
   */
  const invalidateTimerList = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.timerList.all });
  }, [queryClient]);

  // Use stable reference for empty array to prevent unnecessary useEffect triggers
  const timerEntries = useMemo(() => data ?? EMPTY_ARRAY, [data]);

  return {
    timerEntries,
    isLoading,
    isFetching,
    error,
    refetch,
    invalidateTimerList,
  };
};
